import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Minus, Sparkles, Send, MessageCircle, Maximize2, Plus, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useChat } from "@/contexts/ChatContext";
import { useStore } from "@/contexts/StoreContext";
import { ChatMessage } from "./ChatMessage";
import { ChatPreviewPanel } from "./ChatPreviewPanel";
import { ChatImageAttachment, useImageAttachment } from "./ChatImageAttachment";
import { cn } from "@/lib/utils";
import { MESSAGE_LIMITS } from "@/lib/constants";

const onboardingPrompts = [
  "How do I connect my store?",
  "What can ProteasAI do for me?",
  "How does the setup process work?",
  "Tell me about pricing plans",
];

const storePrompts = [
  "Create a new product for me",
  "Which products are low on stock?",
  "What SEO issues need attention?",
  "Show me my store stats",
];

const analyticsPrompts = [
  "Who are my top 5 customers?",
  "What's my best selling product?",
  "What day of the week has most sales?",
  "Compare this month vs last month",
  "Show me revenue trends",
  "What's my customer lifetime value?",
];

const mediaPrompts = [
  "How many images need alt text?",
  "Which images are too large?",
  "Help me optimize my media library",
];

export function ChatDrawer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { stores } = useStore();
  const {
    messages,
    isOpen,
    isMinimized,
    unreadCount,
    context,
    hasStores,
    openChat,
    closeChat,
    minimizeChat,
    sendMessage,
    clearMessages,
    setContext,
    clearContext,
    createNewConversation,
    regenerateResponse,
    regeneratingMessageId,
  } = useChat();

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Image attachment state
  const { attachedImages, setAttachedImages, clearImages, getImageAttachments } = useImageAttachment();

  // Check if we're on analytics page
  const isAnalyticsPage = location.pathname === '/analytics' || location.pathname === '/insights';

  // Route-to-context mapping for automatic context updates
  const routeContextMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/products': 'Products',
    '/seo': 'SEO Issues',
    '/insights': 'Insights',
    '/analytics': 'Analytics',
    '/media': 'Media Library',
    '/settings': 'Settings',
    '/stores': 'Stores',
    '/connect-store': 'Store Setup',
  };

  // Select prompts based on current page and store connection
  const getSuggestedPrompts = () => {
    if (!hasStores) return onboardingPrompts;

    // Show context-specific prompts based on current page
    if (location.pathname === '/analytics' || location.pathname === '/insights') {
      return analyticsPrompts;
    }
    if (location.pathname === '/media') {
      return mediaPrompts;
    }
    return storePrompts;
  };

  const suggestedPrompts = getSuggestedPrompts();

  // Auto-update context based on current route
  useEffect(() => {
    const newContext = routeContextMap[location.pathname] || '';
    if (newContext && newContext !== context) {
      setContext(newContext);
    }
  }, [location.pathname, setContext]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const message = input.trim();
    const images = getImageAttachments();
    
    setInput("");
    setIsLoading(true);

    try {
      await sendMessage(message, images.length > 0 ? images : undefined);
      clearImages();
    } catch (error) {
      console.error("Failed to send message:", error);
      // Keep images attached so the user can retry without re-uploading.
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    // Just close the drawer without clearing messages
    // The conversation will persist and can be accessed from the /chat page
    closeChat();
  };

  const handleOpenFullScreen = () => {
    // Navigate to the full chat page
    // The conversation will automatically continue there since they share the same context
    navigate('/chat');
    closeChat();
  };

  // Floating button when closed or minimized
  if (!isOpen || isMinimized) {
    return (
      <>
        <ChatPreviewPanel />
        <button
          onClick={openChat}
          className={cn(
            "fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg",
            "flex items-center justify-center transition-transform hover:scale-105",
            "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          )}
          title="Ask ProteasAI"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </>
    );
  }

  return (
    <>
      <ChatPreviewPanel />
      <div
        className={cn(
          "fixed bottom-6 right-6 z-50 flex flex-col bg-card border border-border rounded-xl shadow-2xl overflow-hidden",
          "w-[400px] h-[600px]",
          "max-sm:bottom-0 max-sm:right-0 max-sm:w-full max-sm:h-full max-sm:rounded-none"
        )}
      >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="font-semibold text-foreground">ProteasAI</span>
          {isAnalyticsPage && (
            <Badge variant="secondary" className="text-[10px] gap-1">
              <BarChart3 className="h-3 w-3" />
              Analytics
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={createNewConversation}
            title="New chat"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleOpenFullScreen}
            title="Open in full screen"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={minimizeChat}>
            <Minus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Analytics Mode Banner */}
      {isAnalyticsPage && messages.length === 0 && (
        <div className="px-4 py-2 bg-primary/5 border-b border-primary/10">
          <p className="text-xs text-primary font-medium">
            Ask me anything about your sales, customers, or revenue!
          </p>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">How can I help?</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-[280px]">
              {hasStores 
                ? "Products, inventory, orders & SEO – all in one place."
                : "I'm here to help you get started with ProteasAI. Ask me anything!"}
            </p>
            <div className="space-y-2 w-full max-w-[300px]">
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => {
                    setInput(prompt);
                    inputRef.current?.focus();
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm text-foreground transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <ChatMessage 
                key={message.id} 
                message={message} 
                onRegenerate={regenerateResponse}
                isRegenerating={regeneratingMessageId === message.id}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Context Bar */}
      {context && (
        <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Context: <span className="font-medium text-foreground">{context}</span>
          </span>
          <button
            onClick={clearContext}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border">
        {/* Character counter - shown when approaching limit */}
        {input.length > MESSAGE_LIMITS.SOFT_WARNING && (
          <div className="text-xs text-right mb-2 px-1">
            <span className={cn(
              "transition-colors",
              input.length > MESSAGE_LIMITS.WARNING_THRESHOLD && "text-amber-600 font-medium",
              input.length >= MESSAGE_LIMITS.MAX_LENGTH && "text-destructive font-semibold"
            )}>
              {input.length}/{MESSAGE_LIMITS.MAX_LENGTH}
              {input.length >= MESSAGE_LIMITS.MAX_LENGTH && " (limit)"}
            </span>
          </div>
        )}

        <div className="flex gap-2">
          <ChatImageAttachment
            attachedImages={attachedImages}
            onImagesChange={setAttachedImages}
            disabled={isLoading}
            compact
          />
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => {
              const value = e.target.value;
              // Only update if within limit or deleting
              if (value.length <= MESSAGE_LIMITS.MAX_LENGTH) {
                setInput(value);
              }
            }}
            onKeyDown={handleKeyDown}
            maxLength={MESSAGE_LIMITS.MAX_LENGTH}
            placeholder={
              attachedImages.length > 0
                ? `Add ${attachedImages.length} image(s) to...`
                : hasStores
                  ? "Ask anything about your store..."
                  : "Ask me anything..."
            }
            disabled={isLoading}
            className="flex-1"
          />
          <Button onClick={handleSend} disabled={!input.trim() || isLoading} size="icon">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
      </div>
    </>
  );
}
